import React, { useEffect, useState } from 'react';
import { Button, IconButton, Tooltip } from '@mui/material';
import { EmailOutlined, Logout } from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import {
  useAuth, useLoading, useNotification, useRequest,
} from 'shared/hooks';
import {
  deleteUser, endUserSessions, getRoles, updateUser,
} from 'shared/api/admin';
import { DataFormatter } from 'shared/components/data-display/DataFormatter';
import { sendResetEmail } from 'shared/api/auth';
import { TextEditor } from 'shared/components/data-display/editors';
import { withStaticSelect } from 'shared/components/data-display/withSelectors';
import { dateString } from 'shared/utils';
import { BUTTON_COLORS, VARIANTS } from 'shared/constants';
import { InfoModal } from 'shared/components/abstract/interactables';
import PasswordUpdater from 'shared/components/PasswordUpdater';
import { DataReducerActions } from 'shared/reducers';
import { GridWrapper } from 'shared/components/data-display/GridFormatter';
import { getUserPath } from 'shared/routes/admin';

/**
 * @param {Object} props
 * @param {Object} props.state
 * @param {number} props.routeId
 * @param {boolean} props.isProfile
 * @param {Function} props.dispatch
 * @param {Function} props.syncSideNav
 */
export default function UserInfo(props) {
  const {
    state,
    routeId,
    isProfile,
    dispatch,
    syncSideNav,
  } = props;

  const { data: user, dirty, updatedData } = state;
  const { user: currentLoggedInUser, isAdmin } = useAuth();
  const { request, isLoading } = useRequest();
  const userId = routeId || currentLoggedInUser.id;
  const ownProfile = Number(userId) === Number(currentLoggedInUser.id);
  const [roles, setRoles] = useState([]);
  const { setLoading } = useLoading();
  const notification = useNotification();

  useEffect(() => {
    (async () => {
      if (isAdmin) {
        const roleResult = await request(getRoles());
        if (roleResult) {
          setRoles(roleResult);
        }
      } else {
        setRoles([]);
      }
    })();
  }, [isAdmin, request]);

  const handleDelete = async () => {
    const result = await request(deleteUser(userId), 'Deleted user');
    if (result) {
      syncSideNav(getUserPath(userId));
      dispatch({ action: DataReducerActions.set, payload: null });
    }
  };

  const handleClear = () => {
    dispatch({ action: DataReducerActions.clear });
  };

  const handleSave = async () => {
    const result = await request(updateUser(userId, updatedData), 'Updated');
    if (result) {
      dispatch({ action: DataReducerActions.set, payload: result });
      return result;
    }
    return null;
  };

  const sendPasswordResetEmail = async () => {
    setLoading(true);
    try {
      await sendResetEmail(user.email);
      notification.pushSuccess('Sent mail');
    } catch (error) {
      notification.pushError(error);
    } finally {
      setLoading(false);
    }
  };

  const endSessions = async () => {
    const result = await request(endUserSessions(userId), 'Ended sessions');
    if (result) {
      dispatch({
        action: DataReducerActions.set,
        payload: { ...user, activeSessions: 0 },
      });
    }
  };

  const emailDisplay = () => {
    if (isAdmin) {
      return (
        <Tooltip title="Send password reset email">
          <IconButton onClick={sendPasswordResetEmail} color="primary" sx={{ paddingRight: '8px' }}>
            <EmailOutlined />
          </IconButton>
        </Tooltip>
      );
    }
    return null;
  };

  const activeSessionDisplay = () => (
    <Tooltip
      title="End all sessions; user will need to reauthenticate after current access token expires"
    >
      <IconButton onClick={endSessions} color="primary" sx={{ paddingRight: '8px' }}>
        <Logout />
      </IconButton>
    </Tooltip>
  );

  const userDescriptions = [
    {
      editInline: true,
      editor: TextEditor,
      key: 'fullname',
      label: 'Name',
      readOnly: !isAdmin,
    },
    {
      editInline: true,
      editor: TextEditor,
      key: 'username',
      label: 'Username',
      readOnly: !isAdmin,
    },
    {
      editInline: true,
      editKey: 'roleId',
      editor: withStaticSelect({
        disableClearable: true,
        options: roles.map((r) => ({ label: r.name, value: r.id })),
      }),
      key: 'role',
      label: 'Role',
      readOnly: roles.length === 0,
    },
    {
      label: 'Email',
      key: 'email',
      readOnly: true,
      actionButton: emailDisplay(),
    },
    {
      label: 'Active Sessions',
      skipWhen: () => !isAdmin,
      key: 'activeSessions',
      actionButton: activeSessionDisplay(),
    },
    {
      key: 'status',
      label: 'Status',
      skipWhen: () => !isAdmin,
    },
    {
      label: 'Last Authenticated',
      skipWhen: () => !isAdmin,
      formatter: (u) => dateString(u.lastAuthenticated),
    },
    {
      label: 'Password Updated',
      formatter: (u) => dateString(u.passwordCreated),
    },
  ];

  return (
    <Grid container spacing={1} sx={{ justifyContent: 'end' }}>
      <GridWrapper title="User Information" size={12}>
        <DataFormatter
          data={user}
          descriptions={userDescriptions}
          updateValue={(key, value) => {
            dispatch({
              action: DataReducerActions.update,
              payload: { key, value },
            });
          }}
          updatedData={updatedData}
        />
      </GridWrapper>
      <Grid container size={12} spacing={1} sx={{ justifyContent: 'end' }}>
        {!dirty && ownProfile && isProfile && <Grid><PasswordUpdater /></Grid>}
        {isAdmin && dirty && (
          <>
            <Grid>
              <Button
                disabled={!dirty}
                onClick={handleClear}
                variant={VARIANTS.outlined}
              >
                Undo
              </Button>
            </Grid>
            <Grid>
              <Button
                disabled={Object.keys(updatedData).length === 0}
                onClick={handleSave}
                variant={VARIANTS.contained}
              >
                Save
              </Button>
            </Grid>
          </>
        )}
        {isAdmin && !dirty
          && (
            <Grid>
              <InfoModal
                actionCallback={handleDelete}
                actionName="Delete"
                description={`Delete ${user?.username}?`}
                disabled={isLoading}
                color={BUTTON_COLORS.error}
              />
            </Grid>
          )}
      </Grid>
    </Grid>
  );
}
